import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

const TRASH_SVG = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/></svg>'

export default class extends Controller {
  static targets = ["list", "item", "deleteBtn"]
  static values = { reorderUrl: String, destroyUrl: String }

  connect() {
    if (this.hasListTarget) {
      this._sortable = Sortable.create(this.listTarget, {
        handle: "[data-drag-handle]",
        filter: "[data-editable='false']",
        onEnd: () => this._persistOrder(),
        animation: 150
      })
    }
    this._pendingDelete = null
    this._pendingTimeout = null
    this._reorderedDuringSession = false
  }

  disconnect() {
    this._sortable?.destroy()
    if (this._pendingTimeout) clearTimeout(this._pendingTimeout)
  }

  done() {
    if (!this._reorderedDuringSession) return
    const frame = document.getElementById("portfolio-dashboard-content")
    if (frame) frame.src = window.location.href
  }

  edit(event) {
    const item = event.currentTarget.closest("[data-portfolio-preset-manage-target='item']")
    if (!item) return
    let preset
    try { preset = JSON.parse(item.dataset.preset) } catch (_) { return }
    this.dispatch("close")
    document.dispatchEvent(new CustomEvent("portfolio-preset:open-edit", { detail: { preset } }))
    document.dispatchEvent(new CustomEvent("modal:open-by-id", { detail: { id: "portfolio-preset-save" } }))
  }

  async confirmDelete(event) {
    const btn = event.currentTarget
    const item = btn.closest("[data-portfolio-preset-manage-target='item']")
    if (!item) return

    if (this._pendingDelete === btn) {
      this._pendingDelete = null
      if (this._pendingTimeout) clearTimeout(this._pendingTimeout)
      await this._doDelete(item, btn)
      return
    }

    if (this._pendingDelete) this._resetDelete(this._pendingDelete)
    this._pendingDelete = btn
    btn.classList.add("text-red-600", "bg-red-50")
    btn.title = "Click again to confirm"
    btn.innerHTML = '<span class="text-[11px] font-medium">Confirm?</span>'
    this._pendingTimeout = setTimeout(() => this._resetDelete(btn), 3000)
  }

  _resetDelete(btn) {
    btn.classList.remove("text-red-600", "bg-red-50")
    btn.title = "Delete"
    btn.innerHTML = TRASH_SVG
    if (this._pendingDelete === btn) this._pendingDelete = null
  }

  async _doDelete(item, btn) {
    const id = item.dataset.id
    btn.disabled = true
    try {
      const response = await fetch(`${this.destroyUrlValue}/${id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
          Accept: "application/json"
        }
      })
      const data = await response.json()
      if (!response.ok || !data.success) {
        this._showToast((data.errors || ["Delete failed"]).join(", "), "alert")
        btn.disabled = false
        this._resetDelete(btn)
        return
      }
      item.remove()
      this._showToast("View deleted", "notice")
      const url = new URL(window.location.href)
      if (url.searchParams.get("preset") === id) {
        url.searchParams.delete("preset")
      }
      const frame = document.getElementById("portfolio-dashboard-content")
      if (frame) frame.src = url.toString()
    } catch (err) {
      this._showToast(err.message || "Network error", "alert")
      btn.disabled = false
      this._resetDelete(btn)
    }
  }

  async _persistOrder() {
    const ids = this.itemTargets.map(el => el.dataset.id).filter(Boolean)
    try {
      const body = new URLSearchParams()
      ids.forEach(id => body.append("ids[]", id))
      await fetch(this.reorderUrlValue, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body
      })
      this._reorderedDuringSession = true
    } catch (_) { /* noop */ }
  }

  _showToast(message, type = "notice") {
    const toast = document.createElement("div")
    toast.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.textContent = message
    document.body.prepend(toast)
  }
}
